import React, { useSyncExternalStore } from "react";

/**
 * Global offline banner. Subscribes to the browser's `online` / `offline`
 * events via `useSyncExternalStore` (React 18+ idiomatic external-store
 * pattern — avoids hydration mismatches and double-render quirks of
 * useState+useEffect). Renders a compact amber status bar at the top of
 * the app shell when the network is down. Convex caches keep data
 * visible; this banner makes the "stale" status explicit so users don't
 * think their actions silently succeeded.
 *
 * Spec 5 PR 10 — Task 74.
 */
function subscribe(callback: () => void): () => void {
  window.addEventListener("online", callback);
  window.addEventListener("offline", callback);
  return () => {
    window.removeEventListener("online", callback);
    window.removeEventListener("offline", callback);
  };
}

function getSnapshot(): boolean {
  return typeof navigator === "undefined" ? true : navigator.onLine;
}

// SSR/non-browser snapshot. Today Steerly is a desktop+SPA only
// (no Next.js / SSR), but providing this keeps the API correct should
// the React app ever be rendered server-side.
function getServerSnapshot(): boolean {
  return true;
}

export function OfflineBanner() {
  const online = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  if (online) return null;
  return (
    <div className="offline-banner" role="status" aria-live="polite">
      <span className="offline-dot" aria-hidden="true" />
      <span>Offline — showing last-synced data. Some actions are paused.</span>
    </div>
  );
}
